// src/ideas/status.ts
var IDEA_STATUS = {
  /** Submitted by a portal user; not yet triaged. Pre-publication — text has NOT passed the PII gate. */
  SUBMITTED: "submitted",
  /** Published by a CARO triager and open to the board. Phase 1 renders this as "Open" (LD-STATE-NAME). */
  VOTING: "voting",
  /** Waiting for the agent to claim it. Reached by threshold (auto) or a deliberate CARO click. */
  QUEUED: "queued",
  /** An agent holds an atomic claim. Stale after IDEA_CLAIM_TTL_MINUTES, then reaped back to queued. */
  GENERATING: "generating",
  /** A draft spec exists: citations resolved ≥ IDEA_CITATION_RESOLVE_MIN and confidence ≥ threshold. */
  SPEC_READY: "spec_ready",
  /** Generation failed mechanically — timeout, unparseable, thin citations, or low confidence. */
  SPEC_FAILED: "spec_failed",
  /** A human accepted the draft onto the roadmap with a horizon. */
  ROADMAPPED: "roadmapped",
  /** Terminal. Human-marked only — never inferred from a commit, deploy or merge (AC-12). */
  SHIPPED: "shipped",
  /** Terminal. Requires a reason, which is shown to the submitter (AC-14). */
  DECLINED: "declined",
  /** Terminal. Folded into a canonical idea; votes transferred. */
  MERGED: "merged"
};
var IDEA_STATUS_VALUES = [
  IDEA_STATUS.SUBMITTED,
  IDEA_STATUS.VOTING,
  IDEA_STATUS.QUEUED,
  IDEA_STATUS.GENERATING,
  IDEA_STATUS.SPEC_READY,
  IDEA_STATUS.SPEC_FAILED,
  IDEA_STATUS.ROADMAPPED,
  IDEA_STATUS.SHIPPED,
  IDEA_STATUS.DECLINED,
  IDEA_STATUS.MERGED
];
var IDEA_TERMINAL_STATUSES = [
  IDEA_STATUS.SHIPPED,
  IDEA_STATUS.DECLINED,
  IDEA_STATUS.MERGED
];
var IDEA_STATUS_TRANSITIONS = {
  [IDEA_STATUS.SUBMITTED]: [
    IDEA_STATUS.VOTING,
    IDEA_STATUS.QUEUED,
    IDEA_STATUS.DECLINED,
    IDEA_STATUS.MERGED
  ],
  [IDEA_STATUS.VOTING]: [IDEA_STATUS.QUEUED, IDEA_STATUS.DECLINED, IDEA_STATUS.MERGED],
  [IDEA_STATUS.QUEUED]: [IDEA_STATUS.GENERATING],
  [IDEA_STATUS.GENERATING]: [
    IDEA_STATUS.SPEC_READY,
    IDEA_STATUS.SPEC_FAILED,
    // stale claim reaped — the agent died mid-run
    IDEA_STATUS.QUEUED
  ],
  // A human retries explicitly. NEVER automatic, NEVER back to voting.
  [IDEA_STATUS.SPEC_FAILED]: [IDEA_STATUS.QUEUED],
  [IDEA_STATUS.SPEC_READY]: [
    // "regenerate with answers" after the open questions were answered
    IDEA_STATUS.QUEUED,
    IDEA_STATUS.ROADMAPPED,
    // the spec revealed it should not be built — a valuable outcome, not a failure
    IDEA_STATUS.DECLINED
  ],
  [IDEA_STATUS.ROADMAPPED]: [IDEA_STATUS.SHIPPED, IDEA_STATUS.DECLINED],
  [IDEA_STATUS.SHIPPED]: [],
  [IDEA_STATUS.DECLINED]: [],
  [IDEA_STATUS.MERGED]: []
};
function canTransition(from, to) {
  if (from === to) return true;
  return (IDEA_STATUS_TRANSITIONS[from] ?? []).includes(to);
}
function allowedTransitions(from) {
  return IDEA_STATUS_TRANSITIONS[from] ?? [];
}
function isTerminalStatus(status) {
  return IDEA_TERMINAL_STATUSES.includes(status);
}
var IDEA_EVENT = {
  /** Vote count reached IDEA_VOTE_THRESHOLD after IDEA_VOTE_DWELL_HOURS in `voting`. Phase 2. */
  THRESHOLD_REACHED: "THRESHOLD_REACHED",
  /** The agent won the conditional UPDATE and holds the claim. */
  AGENT_CLAIMED: "AGENT_CLAIMED",
  /** A claim went stale and was returned to `queued`. */
  CLAIM_REAPED: "CLAIM_REAPED"
};
var IDEA_TRIGGER = {
  MANUAL: "manual",
  AUTO_THRESHOLD: "auto:threshold",
  AUTO_CLAIM_REAPED: "auto:claim_reaped",
  AUTO_AGENT: "auto:agent",
  AUTO_ROADMAP_IDLE: "auto:roadmap_idle"
};
var IDEA_VOTE_THRESHOLD = 3;
var IDEA_VOTE_DWELL_HOURS = 24;
var IDEA_CLAIM_TTL_MINUTES = 30;
var IDEA_CONFIDENCE_THRESHOLD = 0.6;
var IDEA_CITATION_RESOLVE_MIN = 0.7;
var IDEA_ROADMAP_IDLE_MONTHS = 6;

// src/ideas/uiFeedback.ts
var IDEA_KINDS = ["functional", "ui"];
var UI_FEEDBACK_CATEGORIES = ["layout", "label", "feedback", "speed", "mobile", "a11y"];
var UI_FEEDBACK_MIN_BODY = 10;
var IDEA_FUNCTIONAL_MIN_BODY = 40;
var UI_CONTEXT_LIMITS = {
  route: 200,
  pageLabel: 80,
  tab: 80,
  elementRole: 40,
  elementLabel: 60,
  appVersion: 40,
  elementPath: 5,
  viewportMin: 1,
  viewportMax: 1e4
};
var ELEMENT_KB_ID_RE = /^[a-z0-9][a-z0-9.-]{0,79}$/;
var PAGE_ID_RE = /^[a-z0-9][a-z0-9-]{0,79}$/;
var UI_CONTEXT_KEYS = [
  "route",
  "pageId",
  "pageLabel",
  "tab",
  "categories",
  "elementKbId",
  "elementPath",
  "elementRole",
  "elementLabel",
  "viewport",
  "colorMode",
  "locale",
  "appVersion",
  "pinned"
];
var UI_CONTEXT_SERVER_KEYS = ["elementLabelDropped", "routeUnverified"];
var UI_CONTEXT_PUBLIC_KEYS = ["route", "pageId", "pageLabel", "categories"];
var UI_CONTEXT_AGENT_KEYS = [
  "route",
  "pageId",
  "pageLabel",
  "tab",
  "categories",
  "elementKbId",
  "elementPath",
  "elementRole",
  "viewport",
  "colorMode"
];
var UI_COLOR_MODES = ["light", "dark"];
var PORTAL_LOCALES = ["en", "nl", "fr", "de", "avl", "wvl"];
var PARAM_SEGMENT_RE = /^:[A-Za-z][A-Za-z0-9_]*$/;
var LITERAL_SEGMENT_RE = /^[a-z0-9-]+$/;
var UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
var DIGIT_RUN_RE = /\d{4,}/;
var ALL_DIGITS_RE = /^\d+$/;
function isSafeRoutePattern(route) {
  if (typeof route !== "string") return false;
  if (route.length === 0 || route.length > UI_CONTEXT_LIMITS.route) return false;
  if (route[0] !== "/") return false;
  if (route === "/") return true;
  if (/[?#*\s]/.test(route) || route.includes("..")) return false;
  const segments = route.slice(1).split("/");
  for (const seg of segments) {
    if (seg.length === 0) return false;
    if (seg[0] === ":") {
      if (!PARAM_SEGMENT_RE.test(seg)) return false;
      continue;
    }
    if (!LITERAL_SEGMENT_RE.test(seg)) return false;
    if (UUID_RE.test(seg) || DIGIT_RUN_RE.test(seg) || ALL_DIGITS_RE.test(seg)) return false;
  }
  return true;
}

// src/ideas/piiScan.ts
var PII_PATTERNS = Object.freeze({
  room: [
    /\b(?:kamer|room|chambre|app?t?|nr\.?)\s*\d{1,4}\b/giu,
    /\b\d{3,4}\b/gu
  ],
  name: [
    /\b(?:Mevrouw|Mijnheer|Meneer|Madame|Monsieur|Mw|Dhr|Mme|Mr|M)\.?\s+\p{Lu}\p{Ll}+(?:\s+\p{Lu}\p{Ll}+)?/gu
  ],
  nrn: [/\b\d{2}\.?\d{2}\.?\d{2}-?\d{3}\.?\d{2}\b/gu],
  contact: [
    /\b[\w.+-]+@[\w-]+\.[\w.-]{2,}\b/gu,
    /(?:\+32|0032)[\s./-]?\d(?:[\s./-]?\d){7,8}\b/gu,
    /\b0\d(?:[\s./-]?\d){7,8}\b/gu
  ]
});
function dedupeOverlaps(hits) {
  const sorted = [...hits].sort((a, b) => {
    if (a.field !== b.field) return a.field < b.field ? -1 : 1;
    if (a.offset !== b.offset) return a.offset - b.offset;
    return b.match.length - a.match.length;
  });
  const kept = [];
  for (const hit of sorted) {
    const covered = kept.some(
      (k) => k.field === hit.field && k.pattern === hit.pattern && hit.offset >= k.offset && hit.offset + hit.match.length <= k.offset + k.match.length
    );
    if (!covered) kept.push(hit);
  }
  return kept;
}
function scanField(text, field, hits) {
  if (typeof text !== "string" || text.length === 0) return;
  for (const [family, patterns] of Object.entries(PII_PATTERNS)) {
    for (const pattern of patterns) {
      const re = new RegExp(pattern.source, pattern.flags);
      for (const m of text.matchAll(re)) {
        const match = m[0];
        if (!match) continue;
        hits.push({ pattern: family, match, offset: m.index ?? 0, field });
      }
    }
  }
}
function scanForPii(title, body) {
  const hits = [];
  scanField(title, "title", hits);
  scanField(body, "body", hits);
  return dedupeOverlaps(hits).sort((a, b) => {
    if (a.field !== b.field) return a.field < b.field ? -1 : 1;
    if (a.offset !== b.offset) return a.offset - b.offset;
    return a.pattern < b.pattern ? -1 : 1;
  });
}
var PII_SCAN_CORPUS = Object.freeze([
  ["", "filters verdwijnen bij scrollen"],
  ["Bulk-assign a device", "Doing it one by one takes an afternoon per floor."],
  ["", "Mevrouw Janssens in kamer 204 belt elke nacht"],
  ["Kamer 204", "Dhr. Peeters wacht lang op hulp"],
  ["", "room 1403 and appt 88 need a label"],
  ["", "de bewoner in 305 ziet de knop niet"],
  ["", "sinds 2026 werkt de filter niet meer"],
  ["", "Madame Dubois en chambre 12"],
  ["", "Monsieur Martin Leroy a appel\xE9"],
  ["", "mail jan.peeters@zorggroep.be voor info"],
  ["", "mail jan+tickets@caro.care"],
  ["", "bel 0475 12 34 56 of 09/123.45.67"],
  ["", "bel +32 475 12 34 56"],
  ["", "bel 0032 9 123 45 67"],
  ["", "rijksregister 85.07.30-033.28"],
  ["", "rijksregister 850730033 28 zonder punten"],
  ["Mw. De Smet", "nr. 7 en app 3"],
  ["", "Portal en Asterisk zijn traag in Gent"],
  [null, null],
  ["", ""],
  ["", "kamer 204 en kamer 305 en kamer 204"],
  ["Mr Jones", "ext 6001 ringt niet"]
]);

export { ELEMENT_KB_ID_RE, IDEA_CITATION_RESOLVE_MIN, IDEA_CLAIM_TTL_MINUTES, IDEA_CONFIDENCE_THRESHOLD, IDEA_EVENT, IDEA_FUNCTIONAL_MIN_BODY, IDEA_KINDS, IDEA_ROADMAP_IDLE_MONTHS, IDEA_STATUS, IDEA_STATUS_TRANSITIONS, IDEA_STATUS_VALUES, IDEA_TERMINAL_STATUSES, IDEA_TRIGGER, IDEA_VOTE_DWELL_HOURS, IDEA_VOTE_THRESHOLD, PAGE_ID_RE, PII_PATTERNS, PII_SCAN_CORPUS, PORTAL_LOCALES, UI_COLOR_MODES, UI_CONTEXT_AGENT_KEYS, UI_CONTEXT_KEYS, UI_CONTEXT_LIMITS, UI_CONTEXT_PUBLIC_KEYS, UI_CONTEXT_SERVER_KEYS, UI_FEEDBACK_CATEGORIES, UI_FEEDBACK_MIN_BODY, allowedTransitions, canTransition, isSafeRoutePattern, isTerminalStatus, scanForPii };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map